import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getRouteBySlug, getRouteProgress, saveRouteProgress, resetRouteProgress,
} from '../services/api';
import telegramService from '../services/telegram';
import Loader from '../components/Loader';
import WalkScreen from '../components/walk/WalkScreen';
import WalkFinish from '../components/walk/WalkFinish';
import useWalkGeolocation from '../hooks/useWalkGeolocation';
import { findStartStation } from '../utils/metro';
import { plural } from '../utils/plural';
import { calcSteps, formatElapsed } from '../utils/walk';

const STORAGE_KEY_PREFIX = 'route_progress_';
const TIMER_KEY_PREFIX = 'route_timer_start_';
const TIMER_FINAL_PREFIX = 'route_timer_final_';

// «0» в поле входа — бесплатно, а не цена в ноль рублей
const entryTextOf = (fee) => (
  fee == null || fee === '' ? null : (fee === '0' || fee === 0 ? 'Бесплатно' : fee)
);

/**
 * Место маршрута редакции — в общий вид экрана прогулки (WalkScreen).
 * Данные привязанного места: у точки, вписанной вручную, их нет —
 * тогда карточка собирается на одних текстовых полях точки
 */
const toWalkStop = (stop) => {
  const place = stop.place;
  const images = stop.images?.length ? stop.images : null;
  return {
    key: stop.id,
    status: stop.status,
    title: place?.title,
    point: place?.latitude && place?.longitude ? [place.latitude, place.longitude] : null,
    // Миниатюра: своё фото точки, иначе фото места
    thumb: images?.[0]?.url || place?.images?.[0]?.url || null,
    images,
    imagesCaption: stop.imagesCaption,
    description: stop.description,
    task: stop.task,
    navigationHint: stop.navigationHint,
    audio: stop.audio,
    audioTitle: stop.audioTitle,
    entryText: entryTextOf(stop.entryFee),
    workingHours: place?.workingHours,
    address: place?.address,
    slug: place?.slug,
  };
};

/**
 * Прогулка по маршруту редакции: прогресс, таймер, финиш.
 * Карта, панель места и окна — общий WalkScreen
 */
const RouteWalkingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Метка перезапуска: «Начать заново» открывает тот же путь с новым ?t=
  const [searchParams] = useSearchParams();
  const restartKey = searchParams.get('t') || '';
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stops, setStops] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerRef = useRef(null);
  // id маршрута для записи прогресса: slug в ручку не годится
  const routeIdRef = useRef(null);
  // Идёт сброс — не давать эффекту сохранения записать старое состояние
  const restartingRef = useRef(false);
  // Прогресс поднят с сервера — до этого сохранять нельзя: эффект
  // успевал залить старое состояние поверх серверного
  const progressLoadedRef = useRef(false);

  const geo = useWalkGeolocation();

  useEffect(() => {
    const load = async () => {
      try {
        const response = await getRouteBySlug(slug);
        const data = response.data?.[0];
        if (!data) return navigate(-1);
        setRoute(data);

        const sorted = (data.stops || []).slice().sort((a, b) => a.order - b.order);
        routeIdRef.current = data.id;

        // Источник истины — сервер. Локальные ключи остаются запасным
        // вариантом: сеть на прогулке ненадёжна, а терять прогресс нельзя
        const remote = await getRouteProgress({ routeId: data.id });
        const localRaw = localStorage.getItem(STORAGE_KEY_PREFIX + slug);
        const local = localRaw ? JSON.parse(localRaw) : null;

        let statuses = null;
        let index = 0;
        let finalMs = 0;

        if (remote?.statuses?.length) {
          statuses = remote.statuses;
          index = remote.currentIndex ?? 0;
          finalMs = Number(remote.elapsedMs || 0);
          if (remote.startedAt) {
            localStorage.setItem(TIMER_KEY_PREFIX + slug, String(new Date(remote.startedAt).getTime()));
          }
        } else if (local?.statuses?.length && !restartKey) {
          // Одноразовый перенос: прогулка, начатая до появления сервера,
          // не должна обнуляться.
          //
          // При рестарте (`?t=` в адресе) эту ветку пропускаем: сервер уже
          // очищен намеренно, а в localStorage мог остаться старый прогресс
          // от ещё живого экземпляра страницы — и «перенос» заливал
          // пройденную прогулку обратно, снова показывая экран финиша
          statuses = local.statuses;
          index = local.index ?? 0;
          finalMs = parseInt(localStorage.getItem(TIMER_FINAL_PREFIX + slug) || '0', 10);
          const startedTs = parseInt(localStorage.getItem(TIMER_KEY_PREFIX + slug) || '0', 10);
          saveRouteProgress({
            routeId: data.id,
            statuses,
            currentIndex: index,
            startedAt: startedTs ? new Date(startedTs).toISOString() : new Date().toISOString(),
            ...(index >= sorted.length && finalMs > 0
              ? { finishedAt: new Date().toISOString(), elapsedMs: finalMs }
              : {}),
          });
        }

        if (statuses) {
          setStops(sorted.map((s, i) => ({ ...s, status: statuses[i] || 'upcoming' })));
          setCurrentIndex(index);
          if (index >= sorted.length) {
            setFinished(true);
            if (finalMs > 0) {
              setElapsedMs(finalMs);
              // Возвращаем ключ в localStorage: по нему страница маршрута
              // понимает, что прогулка завершена, и показывает «Пройти
              // заново» вместо «Начать маршрут»
              localStorage.setItem(TIMER_FINAL_PREFIX + slug, String(finalMs));
            }
          }
        } else {
          // Рестарт: чистим локальные ключи, чтобы старая прогулка
          // не всплыла при следующей загрузке
          if (restartKey) {
            localStorage.removeItem(STORAGE_KEY_PREFIX + slug);
            localStorage.removeItem(TIMER_FINAL_PREFIX + slug);
          }
          // Прогресса нет ни на сервере, ни локально — начинаем с нуля.
          // Сбрасывать нужно и финиш: страница живёт в кеше
          // (keepalive-for-react) и при «Начать заново» не перемонтируется
          setStops(sorted.map((s, i) => ({ ...s, status: i === 0 ? 'active' : 'upcoming' })));
          setCurrentIndex(0);
          setFinished(false);
          setElapsedMs(0);
        }
      } catch {
        navigate(-1);
      } finally {
        progressLoadedRef.current = true;
        setLoading(false);
      }
    };
    progressLoadedRef.current = false;
    load();
    // restartKey — метка `?t=` из «Начать заново». Slug при рестарте
    // не меняется, страница живёт в кеше и эффект не перезапускался:
    // на экране оставалась пройденная прогулка
  }, [slug, restartKey, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stops.length === 0) return;
    // Во время сброса не сохраняем: маршрут открыт по тому же slug,
    // компонент не перемонтируется, и эффект успевал записать старые
    // статусы обратно — сразу после того, как reset их удалил
    if (restartingRef.current) return;
    if (!progressLoadedRef.current) return;
    const statuses = stops.map((s) => s.status);

    // Локально пишем всегда: сервер может не ответить, а прогулка
    // должна пережить и потерю сети, и перезагрузку страницы
    localStorage.setItem(
      STORAGE_KEY_PREFIX + slug,
      JSON.stringify({ statuses, index: currentIndex })
    );

    if (!routeIdRef.current) return;
    const startedTs = parseInt(localStorage.getItem(TIMER_KEY_PREFIX + slug) || '0', 10);
    saveRouteProgress({
      routeId: routeIdRef.current,
      statuses,
      currentIndex,
      ...(startedTs ? { startedAt: new Date(startedTs).toISOString() } : {}),
    });
  }, [stops, currentIndex, slug]);

  // Таймер маршрута
  useEffect(() => {
    // Если маршрут уже завершён — финальное время уже установлено при загрузке
    if (finished) return undefined;
    const startTs = parseInt(localStorage.getItem(TIMER_KEY_PREFIX + slug) || '0', 10);
    if (!startTs) {
      localStorage.setItem(TIMER_KEY_PREFIX + slug, Date.now().toString());
    }
    timerRef.current = setInterval(() => {
      const ts = parseInt(localStorage.getItem(TIMER_KEY_PREFIX + slug) || Date.now().toString(), 10);
      setElapsedMs(Date.now() - ts);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [slug, finished]);

  // Последнее место отмечено — фиксируем итоговое время. Итог уходит
  // на сервер вместе с отметкой о финише
  const finish = useCallback(() => {
    clearInterval(timerRef.current);
    const finalMs = Date.now() - parseInt(localStorage.getItem(TIMER_KEY_PREFIX + slug) || Date.now(), 10);
    localStorage.setItem(TIMER_FINAL_PREFIX + slug, finalMs.toString());
    setElapsedMs(finalMs);
    setFinished(true);
    if (routeIdRef.current) {
      saveRouteProgress({
        routeId: routeIdRef.current,
        finishedAt: new Date().toISOString(),
        elapsedMs: finalMs,
      });
    }
  }, [slug]);

  // Текущее место — в статус «пройдено» или «пропущено», дальше следующее
  const leaveCurrent = useCallback((status) => {
    setStops((prev) => prev.map((s, i) => {
      if (i === currentIndex) return { ...s, status };
      if (i === currentIndex + 1) return { ...s, status: 'active' };
      return s;
    }));
    const next = currentIndex + 1;
    setCurrentIndex(next);
    if (next >= stops.length) finish();
  }, [currentIndex, stops.length, finish]);

  // Отметил место по ошибке — можно вернуться. Раньше единственным откатом
  // было «Начать заново» на финише, стирающее всю прогулку
  const goBackStop = useCallback(() => {
    setCurrentIndex((idx) => {
      const prev = Math.max(0, idx - 1);
      setStops((list) => list.map((s, i) => {
        if (i === prev) return { ...s, status: 'active' };
        if (i === idx) return { ...s, status: 'upcoming' };
        return s;
      }));
      return prev;
    });
  }, []);

  const handleRestart = async () => {
    restartingRef.current = true;
    localStorage.removeItem(STORAGE_KEY_PREFIX + slug);
    localStorage.removeItem(TIMER_KEY_PREFIX + slug);
    localStorage.removeItem(TIMER_FINAL_PREFIX + slug);
    localStorage.setItem(TIMER_KEY_PREFIX + slug, Date.now().toString());
    // Серверную запись тоже стираем и ждём ответа: иначе загрузка
    // следующей страницы прочитает старый прогресс и вернёт всё обратно
    if (routeIdRef.current) {
      await resetRouteProgress({ routeId: routeIdRef.current });
    }
    // Состояние сбрасываем руками: маршрут открывается по тому же slug,
    // компонент не перемонтируется
    setStops((prev) => prev.map((s, i) => ({ ...s, status: i === 0 ? 'active' : 'upcoming' })));
    setCurrentIndex(0);
    setElapsedMs(0);
    setFinished(false);
    restartingRef.current = false;
    navigate(`/routes/${slug}/walk?t=${Date.now()}`, { replace: true });
  };

  if (loading) return <Loader />;

  const walkStops = stops.map(toWalkStop);

  if (finished) {
    const doneCount = stops.filter((s) => s.status === 'done').length;
    const skippedCount = stops.filter((s) => s.status === 'skipped').length;
    const steps = calcSteps(route?.distance || 0);
    const results = [
      { value: formatElapsed(elapsedMs), label: 'в пути' },
      route?.distance > 0 && {
        value: `${String(route.distance).replace('.', ',')} км`,
        label: 'пройдено',
      },
      steps && { value: `~${steps.toLocaleString('ru-RU')}`, label: 'шагов' },
      {
        value: `${doneCount} из ${stops.length}`,
        label: skippedCount > 0
          ? `${plural(stops.length, ['места', 'мест', 'мест'])}, ${skippedCount} пропущено`
          : plural(stops.length, ['места', 'мест', 'мест']),
      },
    ].filter(Boolean);

    const shareResult = () => {
      telegramService.hapticFeedback('light');
      const shareUrl = `https://t.me/spbneformal_app_bot/apps?startapp=route-${slug}`;
      // Текст уходит в Telegram, а не в интерфейс — символ тут допустим (брендбук 9.3)
      const shareText = `Прошёл маршрут «${route?.title}» за ${formatElapsed(elapsedMs)} 🚶`;
      telegramService.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
      );
    };

    return (
      <WalkFinish
        title={route?.title}
        results={results}
        stops={walkStops}
        onShare={shareResult}
        secondary={{ label: 'Другие маршруты', onClick: () => navigate('/routes') }}
        onRestart={handleRestart}
      />
    );
  }

  const station = findStartStation(stops[0]?.place);

  return (
    <WalkScreen
      stops={walkStops}
      currentIndex={currentIndex}
      elapsedMs={elapsedMs}
      geo={geo}
      startStation={station}
      onArrived={() => leaveCurrent('done')}
      onSkip={() => leaveCurrent('skipped')}
      onBack={goBackStop}
    />
  );
};

export default RouteWalkingPage;
